import React, { useEffect, useState, useRef } from 'react';
interface LazyMountProps {
  children: ReactNode;
  /** Distance before viewport to start loading (e.g. "200px"). Smaller = lazier. */
  rootMargin?: string;
  /** Fallback placeholder height to prevent layout shift */
  minHeight?: number | string;
  /** Render immediately, skipping observer (e.g. above-the-fold) */
  eager?: boolean;
}
/**
 * LazyMount delays mounting its children until the placeholder enters the viewport
 * (with configurable rootMargin). The placeholder reserves vertical space so the
 * page actually has scroll height — without that, every section below the hero
 * would already be near the viewport and all observers would fire at once.
 */
export function LazyMount({
  children,
  rootMargin = '150px',
  minHeight = '80vh',
  eager = false
}: LazyMountProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shouldRender, setShouldRender] = useState(eager);
  useEffect(() => {
    if (shouldRender) return;
    if (typeof IntersectionObserver === 'undefined') {
      setShouldRender(true);
      return;
    }
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) {
        setShouldRender(true);
        observer.disconnect();
      }
    }, {
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [shouldRender, rootMargin]);
  if (shouldRender) {
    return <>{children}</>;
  }
  return <div ref={ref} aria-hidden="true" style={{
    minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight
  }} data-id="element-195" />;
}